import { useMemo, useState } from "react";
import {
  ShieldAlert, Calendar, Wallet, ChevronDown, ChevronUp, Clock, AlertTriangle,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Italian date parser — handles "DD/MM/YYYY", "DD-MM-YYYY", "DD MMM YYYY".
// Returns a Date or null if not parseable.
// -----------------------------------------------------------------------------
const ITA_MONTHS = {
  gennaio: 0, gen: 0, "01": 0, "1": 0,
  febbraio: 1, feb: 1, "02": 1, "2": 1,
  marzo: 2, mar: 2, "03": 2, "3": 2,
  aprile: 3, apr: 3, "04": 3, "4": 3,
  maggio: 4, mag: 4, "05": 4, "5": 4,
  giugno: 5, giu: 5, "06": 5, "6": 5,
  luglio: 6, lug: 6, "07": 6, "7": 6,
  agosto: 7, ago: 7, "08": 7, "8": 7,
  settembre: 8, set: 8, sett: 8, "09": 8, "9": 8,
  ottobre: 9, ott: 9, "10": 9,
  novembre: 10, nov: 10, "11": 10,
  dicembre: 11, dic: 11, "12": 11,
};

const parseItalianDate = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim().toLowerCase();
  // ISO: YYYY-MM-DD (may be embedded — first match wins)
  let m = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return isNaN(d.getTime()) ? null : d;
  }
  // DD/MM/YYYY or DD-MM-YYYY (only one match, may be embedded)
  m = s.match(/(\d{1,2})[\/.](\d{1,2})[\/.](\d{2,4})/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10) - 1;
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  // DD MMM YYYY  (e.g. "15 marzo 2025")
  m = s.match(/(\d{1,2})\s+([a-z]+)\s+(\d{4})/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = ITA_MONTHS[m[2]];
    const year = parseInt(m[3], 10);
    if (month === undefined) return null;
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  // MMM YYYY  (e.g. "marzo 2025") — assume day 1
  m = s.match(/^([a-z]+)\s+(\d{4})$/);
  if (m) {
    const month = ITA_MONTHS[m[1]];
    const year = parseInt(m[2], 10);
    if (month === undefined) return null;
    const d = new Date(year, month, 1);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatItDate = (d) =>
  d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const daysFromNow = (d) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - now) / 86_400_000);
};

// -----------------------------------------------------------------------------
// KPI Banner — sticky compact bar with risks count, next deadline, contract value.
// Click chevron to expand. Risks block / next deadline are clickable shortcuts:
//   - risks  → jump to the appropriate risks/safety tab
//   - deadline → open Promemoria dialog (or jump to a deadlines tab fallback)
// -----------------------------------------------------------------------------
const KpiBanner = ({
  rischi = [],
  scadenze = [],
  valoreContratto,
  piano = [],
  onJumpToRisks,
  onJumpToDeadlines,
  onOpenReminders,
}) => {
  const [expanded, setExpanded] = useState(false);

  const counts = useMemo(() => {
    const c = { HIGH: 0, MEDIUM: 0, LOW: 0 };
    (rischi || []).forEach((r) => {
      if (c[r.severita] !== undefined) c[r.severita] += 1;
    });
    return c;
  }, [rischi]);

  // Find next imminent deadline (future or today).
  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const enriched = (scadenze || [])
      .map((s) => ({ ...s, _date: parseItalianDate(s.data) }))
      .filter((s) => s._date && s._date >= today)
      .sort((a, b) => a._date - b._date);
    return enriched;
  }, [scadenze]);

  const next = upcoming[0] || null;
  const totalActions = Array.isArray(piano) ? piano.length : 0;

  // Banner is hidden if no meaningful data at all.
  const hasAnyData =
    counts.HIGH + counts.MEDIUM + counts.LOW > 0 ||
    upcoming.length > 0 ||
    (valoreContratto && valoreContratto !== "Non specificato");
  if (!hasAnyData) return null;

  const nextDays = next ? daysFromNow(next._date) : null;
  const urgentNext = nextDays !== null && nextDays <= 7;

  const risksClickable = typeof onJumpToRisks === "function";
  const deadlineAction = onOpenReminders || onJumpToDeadlines || null;
  const deadlineClickable = next && typeof deadlineAction === "function";
  const deadlineHint = onOpenReminders ? "Apri Promemoria" : "Vai alle scadenze";

  return (
    <div
      data-testid="kpi-banner"
      className="sticky top-16 z-30 -mx-4 sm:-mx-6 mb-5"
    >
      <div className="bg-brand-navy text-white shadow-lg border-b border-brand-orange/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5">
          {/* Compact row */}
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap text-xs sm:text-sm">
            {/* Risks (clickable → jumps to risks/safety tab) */}
            {risksClickable ? (
              <button
                type="button"
                data-testid="kpi-risks"
                onClick={onJumpToRisks}
                title="Vai ai rischi"
                className="group flex items-center gap-2 shrink-0 -mx-1 px-1 py-0.5 rounded hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-orange/60"
              >
                <ShieldAlert size={15} className="text-brand-orange shrink-0" />
                <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider text-slate-300 group-hover:text-white">
                  Rischi
                </span>
                <span data-testid="kpi-risks-high" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> {counts.HIGH}
                </span>
                <span data-testid="kpi-risks-medium" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-amber-400" /> {counts.MEDIUM}
                </span>
                <span data-testid="kpi-risks-low" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> {counts.LOW}
                </span>
              </button>
            ) : (
              <div data-testid="kpi-risks" className="flex items-center gap-2 shrink-0" title="Rischi rilevati per livello di gravità">
                <ShieldAlert size={15} className="text-brand-orange shrink-0" />
                <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider text-slate-300">
                  Rischi
                </span>
                <span data-testid="kpi-risks-high" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> {counts.HIGH}
                </span>
                <span data-testid="kpi-risks-medium" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-amber-400" /> {counts.MEDIUM}
                </span>
                <span data-testid="kpi-risks-low" className="inline-flex items-center gap-1 font-mono font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> {counts.LOW}
                </span>
              </div>
            )}

            <span className="hidden sm:block h-4 w-px bg-white/15" aria-hidden />

            {/* Next deadline (clickable → opens reminders or jumps to deadlines tab) */}
            {deadlineClickable ? (
              <button
                type="button"
                data-testid="kpi-next-deadline"
                onClick={deadlineAction}
                title={deadlineHint}
                className="flex items-center gap-2 min-w-0 flex-1 -mx-1 px-1 py-0.5 rounded hover:bg-white/10 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-brand-orange/60"
              >
                <Calendar size={15} className="text-brand-orange shrink-0" />
                <span className="inline-flex items-center gap-2 min-w-0">
                  <span className="font-semibold truncate max-w-[140px] sm:max-w-[260px]">
                    {next.titolo || next.descrizione || "Scadenza"}
                  </span>
                  <span className="font-mono text-slate-200 shrink-0">{formatItDate(next._date)}</span>
                  {nextDays !== null && (
                    <span
                      data-testid="kpi-next-deadline-days"
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                        urgentNext ? "bg-brand-orange text-white" : "bg-white/15 text-white"
                      }`}
                    >
                      {urgentNext && <AlertTriangle size={10} />}
                      {nextDays === 0 ? "Oggi" : nextDays === 1 ? "Domani" : `Tra ${nextDays} gg`}
                    </span>
                  )}
                </span>
              </button>
            ) : (
              <div data-testid="kpi-next-deadline" className="flex items-center gap-2 min-w-0 flex-1" title="Scadenza più imminente con data certa">
                <Calendar size={15} className="text-brand-orange shrink-0" />
                {next ? (
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className="font-semibold truncate max-w-[140px] sm:max-w-[260px]">
                      {next.titolo || next.descrizione || "Scadenza"}
                    </span>
                    <span className="font-mono text-slate-200 shrink-0">{formatItDate(next._date)}</span>
                    {nextDays !== null && (
                      <span
                        data-testid="kpi-next-deadline-days"
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                          urgentNext ? "bg-brand-orange text-white" : "bg-white/15 text-white"
                        }`}
                      >
                        {urgentNext && <AlertTriangle size={10} />}
                        {nextDays === 0 ? "Oggi" : nextDays === 1 ? "Domani" : `Tra ${nextDays} gg`}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-slate-300 italic">Nessuna scadenza datata</span>
                )}
              </div>
            )}

            <span className="hidden md:block h-4 w-px bg-white/15" aria-hidden />

            {/* Contract value */}
            <div
              data-testid="kpi-value"
              className="hidden md:flex items-center gap-2 shrink-0"
              title="Valore complessivo del contratto"
            >
              <Wallet size={15} className="text-brand-orange shrink-0" />
              <span className="font-mono font-bold text-slate-100 truncate max-w-[180px]">
                {valoreContratto && valoreContratto !== "Non specificato"
                  ? valoreContratto
                  : "Valore non specificato"}
              </span>
            </div>

            {/* Expand toggle */}
            <button
              type="button"
              data-testid="kpi-toggle-btn"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? "Comprimi riepilogo" : "Espandi riepilogo"}
              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-slate-200 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            >
              {expanded ? "Comprimi" : "Dettagli"}
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          {/* Expanded panel */}
          {expanded && (
            <div
              data-testid="kpi-expanded"
              className="mt-3 pt-3 border-t border-white/10 grid sm:grid-cols-3 gap-4 text-xs"
            >
              {/* Contract value (mobile) + summary */}
              <div className="space-y-1 md:hidden">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Valore contratto
                </div>
                <div className="font-mono font-bold">
                  {valoreContratto && valoreContratto !== "Non specificato"
                    ? valoreContratto
                    : "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Riepilogo rischi
                </div>
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Alto</span>
                    <span className="font-mono font-bold">{counts.HIGH}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Medio</span>
                    <span className="font-mono font-bold">{counts.MEDIUM}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Basso</span>
                    <span className="font-mono font-bold">{counts.LOW}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Clock size={11} /> Prossime scadenze
                </div>
                {upcoming.length === 0 ? (
                  <p className="text-slate-300 italic">Nessuna scadenza con data certa.</p>
                ) : (
                  <ul className="space-y-1" data-testid="kpi-upcoming-list">
                    {upcoming.slice(0, 3).map((s, i) => {
                      const days = daysFromNow(s._date);
                      const urgent = days <= 7;
                      return (
                        <li
                          key={`up-${i}`}
                          data-testid={`kpi-upcoming-${i}`}
                          className="flex items-center gap-2"
                        >
                          <span className="font-mono text-slate-300 shrink-0 w-20">
                            {formatItDate(s._date)}
                          </span>
                          <span className="truncate flex-1">{s.titolo || s.descrizione}</span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0 ${
                              urgent ? "bg-brand-orange text-white" : "bg-white/15"
                            }`}
                          >
                            {days === 0 ? "Oggi" : days === 1 ? "Domani" : `${days} gg`}
                          </span>
                        </li>
                      );
                    })}
                    {upcoming.length > 3 && (
                      <li className="text-slate-400 text-[11px] italic">
                        + altre {upcoming.length - 3} scadenze nella vista completa
                      </li>
                    )}
                  </ul>
                )}
                {totalActions > 0 && (
                  <p className="pt-1 text-[11px] text-slate-300">
                    Piano d'azione: <b className="text-white">{totalActions}</b>{" "}
                    {totalActions === 1 ? "azione" : "azioni"} da pianificare.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KpiBanner;
